import React, { useState, useEffect, useRef } from "react";
import styles from "./EditExchangeForm.module.css"
import { useFont } from "../../../contexts/FontContext/FontContext";
import BackButton from "../../BackButton/BackButton";
import ToggleSwitch from "../../ToggleSwitch/ToggleSwitch";
import SelectEligibleProducts from "./SelectEligibleProducts";
import { getAllChoices, getExchangeProductChoices, getDefaultPackageWeightAndDimensions } from "../../utils/common";

const EditExchangeForm = ( { showSelectEligibleProducts, setShowSelectEligibleProducts, selectedProduct } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const [choices, setChoices] = useState({}); //all feedback reasons for the selectedProduct
	const [allExchangeReasons, setAllExchangeReasons] = useState({}); //all choices configured on the default form
	const [highlightIndex, setHighlightIndex] = useState(null);
	const [defaultSwitchState, setDefaultSwitchState] = useState(false);
	const [returnsSwitchState, setReturnsSwitchState] = useState(false);
	const [exchangesSwitchState, setExchangesSwitchState] = useState(false);
	const [returnsDefaultSwitchState, setReturnsDefaultSwitchState] = useState(false);
	const [exchangesDefaultSwitchState, setExchangesDefaultSwitchState] = useState(false);
	const [renderProductDimensionsConfig, setRenderProductDimensionsConfig] = useState(false);
	const [revealChoicesOptionsBox, setRevealChoicesOptionsBox] = useState(false); //displays the choices configured on default form
	const [selectedReason, setSelectedReason] = useState(null);
	const [length, setLength] = useState(1);
	const [width, setWidth] = useState(1);
	const [height, setHeight] = useState(1);
	const [weight, setWeight] = useState(1);
	const [defaultLength, setDefaultLength] = useState(1);
	const [defaultWidth, setDefaultWidth] = useState(1);
	const [defaultHeight, setDefaultHeight] = useState(1);
	const [defaultWeight, setDefaultWeight] = useState(1);
	const [loading, setLoading] = useState(false);
	const token = localStorage.getItem("authToken");
	const choicesBoxRef = useRef(null); //used for auto scrolling choices box to bottom so that new added choice is always visible right away
	const fontFamilyOne = useFont().scheme_one;
	const maxChoices = 4;
	const reasonIndex = 0, subReasonsIndex = 1, defaultToggleIndex = 2;

	useEffect(() => { 
		const init = async () => {
	      	setLoading(true);
	      	setAllExchangeReasons(await getAllChoices(apiUrl, token));
	      	setChoices(await getExchangeProductChoices(apiUrl, token, selectedProduct.product_id));
	      	await fetchProductWeightAndDimensions();
	      	const defaults = await getDefaultPackageWeightAndDimensions(apiUrl, token);
			setDefaultLength(defaults.length);
			setDefaultWidth(defaults.width);
			setDefaultHeight(defaults.height);
			setDefaultWeight(defaults.weight);
	      	await fetchSwitchStates();
		    setLoading(false);    
	  	};
	  	init();
	}, []);

	//Attach a new exchange reason to the selectedProduct
	const addNewExchangeProductReason = async (exchangeProductId, exchangeReasonPk) => {
		try {
			const response = await fetch(`${apiUrl}/api/attach_reason_to_exchange_product/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ exchangeReasonPk, exchangeProductId })
			});
			const data = await response.json();
		} catch (error) { console.error("Error attaching reason to product", error); }
		setChoices(prevChoices => ({...prevChoices, [exchangeReasonPk]: allExchangeReasons[exchangeReasonPk]}));
		setRevealChoicesOptionsBox(false);
	}

	//retrieve states for all switches
	const fetchSwitchStates = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/fetch_switch_states/?product_id=${encodeURIComponent(selectedProduct.product_id)}`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
			});
			const data = await response.json();
			setDefaultSwitchState(data.default_choices_switch);
			setReturnsSwitchState(data.returns_switch);
			setReturnsDefaultSwitchState(data.default_returns_switch);
			setExchangesSwitchState(data.exchanges_switch);
			setExchangesDefaultSwitchState(data.default_exchanges_switch);
		} catch (error) { console.error("Error fetching switch states:", error); }
	}

	//retrieve current values for weight and dimensions
	const fetchProductWeightAndDimensions = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/fetch_exchange_product_weight_and_dimensions/?product_id=${encodeURIComponent(selectedProduct.product_id)}`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}`,
				},
			});
			const data = await response.json();
			setLength(data.length);
			setWidth(data.width);
			setHeight(data.height);
			setWeight(data.weight);
		} catch (err) { console.error("Failed to fetch dimensions:", err); }
	};

	//edit choice text
	const handleChoiceChange = (index, newValue) => {
		const updatedChoices = [...choices];
		updatedChoices[index] = newValue;
		setChoices(updatedChoices);
	};

	//update state of selected switch in database
	const handleReasonFormSwitch = async (switch_) => {
		try {
			const response = await fetch(`${apiUrl}/api/toggle_reason_form_switch/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ switch: switch_, product_id: selectedProduct.product_id })
			});
		} catch (error) { console.error(`Error toggling ${switch_} switch:`, error); }
	} 

	const setDefaultWeightAndDimensions = async () => {
		setLength(defaultLength);
		setWidth(defaultWidth);
		setHeight(defaultHeight);
		setWeight(defaultWeight);
		const data = { length: defaultLength, width: defaultWidth, height: defaultHeight, weight: defaultWeight };
		await updateProductWeightAndDimensions(data);
	}

	//show default choices or choices tied to this particular exchange product
	const handleDefaultChoicesSwitch = async () => {
		if (!defaultSwitchState) { 
			setReturnsSwitchState(returnsDefaultSwitchState);
			setExchangesSwitchState(exchangesDefaultSwitchState);
			await handleReasonFormSwitch("");
			await setDefaultWeightAndDimensions(); 
		}
		setDefaultSwitchState(!defaultSwitchState);
		try {
			const response = await fetch(`${apiUrl}/api/toggle_exchange_product_default_reasons_switch/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ product_id: selectedProduct.product_id })
			});
		} catch (error) { console.error("Error toggling default reasons switch:", error); }
	}

	//remove from ui and detach reason from exchange product in the db
	const handleRemoveChoice = async (exchangeProductId, exchangeReasonPk) => { 
		try {
			const response = await fetch(`${apiUrl}/api/detach_reason_from_exchange_product/`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"Authorization": `Bearer ${token}`,
				},
				body: JSON.stringify({ exchangeReasonPk, exchangeProductId })
			});
		} catch (error) { console.error("Error detaching reason from product", error); }
		setChoices(prevChoices => {
			const updated = { ...prevChoices };
		  	delete updated[exchangeReasonPk];
		  	return updated;
		});
	}

	const updateProductWeightAndDimensions = async (data) => {
		try {
	    	await fetch(`${apiUrl}/api/update_exchange_product_weight_and_dimensions/`, {
      		method: "POST",
      		headers: {
        		"Content-Type": "application/json",
        		Authorization: `Bearer ${token}`,
      		},
	      	body: JSON.stringify({product_id: selectedProduct.product_id, ...data, })});
		} catch (err) { console.error("Failed to update dimensions:", err); }
	};

	return (
		<>
			{showSelectEligibleProducts && (<SelectEligibleProducts showSelectEligibleProducts={showSelectEligibleProducts} setShowSelectEligibleProducts={setShowSelectEligibleProducts} />)}
			{!showSelectEligibleProducts && (
				<div className={`${styles.edit_exchange_form_container} ${fontFamilyOne}`}>
					<p className={`titles ${styles.edit_exchange_form_title} fade_in`}>{selectedProduct.product_title}</p>
					<div className={styles.edit_exchange_form_box}>
						{loading && (<img className={styles.feedback_choices_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
						{!loading && !renderProductDimensionsConfig && (
							<>
								<p className={`titles ${styles.add_feedback_title} fade_in`}>{selectedReason ? "Showing sub reasons." : revealChoicesOptionsBox ? "Select a choice." : "Edit feedback selection for customers."}</p>
								{revealChoicesOptionsBox && ( /*Select a reason from list of all configured reasons */
									<>
										<img src={`${process.env.PUBLIC_URL}/images/remove_popup.png`} alt={"remove_choices_options_box"} className={`${styles.remove_choices_options_box}`} onClick={() => { setRevealChoicesOptionsBox(false); }} />
										<div className={`${styles.choices_options_box} fade_in`} ref={choicesBoxRef}>										
											{Object.keys(allExchangeReasons).length === 0 ? (
												<p className={styles.no_choices_configured}>No choices configured.</p>
												) : (
													Object.entries(allExchangeReasons).map(([id, reasonArr], index) => ( //this format:  {reason pk: ["edit new choice", {subPk: "sub reason"}, false, false]}
														<div key={id} className={`${styles.choice_item} ${styles.select}`} onClick={() => addNewExchangeProductReason(selectedProduct.product_id, id)}> 
															<div className={`${styles.choice_text_wrapper} ${styles.select}`}>
													    		<p className={styles.choice_text}>{reasonArr[reasonIndex]}</p> 
													    	</div>
													  	</div>
													))
												)
											}
										</div>
									</>
								)}
								{!revealChoicesOptionsBox && (
									(!defaultSwitchState && Object.keys(choices).length === 0) || (defaultSwitchState && Object.values(allExchangeReasons).filter(reasonArr => reasonArr[defaultToggleIndex] === true).length === 0) ? (
										<p className={styles.no_options}>No options added.</p>
										) : (
									    <div className={`${styles.choices_box} fade_in`} ref={choicesBoxRef}>		
									    	{selectedReason ? ( /*Render sub reasons */
									    		(() => {
												    const source = defaultSwitchState ? allExchangeReasons[selectedReason] : choices[selectedReason];
												    if (!source) { return <p className={styles.no_sub_reasons}>No sub reasons configured.</p>; }
												    const subReasons = source[subReasonsIndex] || {};
												    return Object.keys(subReasons).length === 0 ? (<p className={styles.no_sub_reasons}>No sub reasons configured.</p>)												    	
												    : (
												      	Object.entries(subReasons).map(([id, subReason]) => (
												        	<div key={`${selectedReason}-${id}`} className={`${styles.choice_item} ${highlightIndex === `${selectedReason}-${id}` ? styles.highlight_wrapper : ""} fade_in`}>
												          		<div className={styles.choice_text_wrapper}>
												          			<p className={styles.choice_text}>{subReason}</p>
												          		</div>
												        	</div>
												      	))
												    );
												})()
										    ) : (
										    	/*Show all reasons with default set to true if defaults are switched on otherwise show all exchange reasons associated specifically with selectedProduct*/
										    	(defaultSwitchState ? Object.entries(allExchangeReasons).filter(([id, reasonArr]) => reasonArr[defaultToggleIndex]) : Object.entries(choices)).map(([id, reasonArr]) => (									
											       	<div key={id} className={`${styles.choice_item} ${highlightIndex === id ? styles.highlight_wrapper : ""} fade_in`}>  
											       		<div className={styles.choice_text_wrapper}>
											        		<p className={styles.choice_text}>{reasonArr[reasonIndex]}</p>
											        	</div>
											        	{Object.keys(reasonArr[subReasonsIndex]).length > 0 && (<div className={`${styles.see_sub_reasons_button} ${defaultSwitchState ? styles.shift : ""}`} onClick={() => setSelectedReason(id)}>sub reasons</div>)}
											        	{!defaultSwitchState && (<img className={styles.remove_choice_button} src={`${process.env.PUBLIC_URL}/images/remove_item.png`} onClick={() => handleRemoveChoice(selectedProduct.product_id, id)} />)}
											        </div>
											    ))
										    )}
									    </div>
									)
								)}
								<div className={styles.reason_options_wrapper}>
									{selectedReason ? (<div className={`login_buttons fade_in ${styles.back_to_main_reasons_button}`} onClick={() => { setSelectedReason(null); }}>&#8678; Back</div>)
										: (<div className={`login_buttons fade_in ${styles.add_option_button} ${Object.keys(choices).length === maxChoices || defaultSwitchState ? styles.dim : ""}`} onClick={() => { Object.keys(choices).length < maxChoices && !defaultSwitchState && setRevealChoicesOptionsBox(true); }}>Add option</div>)	
									}
									<div className={styles.default_options_switch_container}>
										<p className={styles.default_switch_text}>Use defaults</p>
										<ToggleSwitch isOn={defaultSwitchState} onToggle={() => { handleDefaultChoicesSwitch(); }} />
									</div>
								</div>
								<div className={`${styles.switch_box} fade_in`}>
									<div className={styles.switch_container}>
										<p className={styles.switch_text}>Show with returns</p>
										<ToggleSwitch isOn={returnsSwitchState} onToggle={() => { setReturnsSwitchState(!returnsSwitchState); handleReasonFormSwitch("returns")} } />
									</div>
									<div className={styles.switch_container}>
										<p className={styles.switch_text}>Show with exchanges</p>
										<ToggleSwitch isOn={exchangesSwitchState} onToggle={() => { setExchangesSwitchState(!exchangesSwitchState); handleReasonFormSwitch("exchanges")} } />
									</div>
								</div>
								<div className={`login_buttons ${styles.select_products_button} fade_in`} onClick={() => setRenderProductDimensionsConfig(true)}>Weight and dimensions</div> 
							</>
						)}
						{!loading && renderProductDimensionsConfig && (
							<div className={`fade_in ${styles.weight_and_dimensions_wrapper}`}>
								<p className={styles.weight_and_dimensions_title}>Configure weight and dimensions.</p>
								<img className={styles.product_dimensions_image} src={selectedProduct.product_image} />
								<div className={styles.dimensions_input_group}>
							    	<div className={styles.dimension_input}>
							        	<label>Length (in.)</label>
							        	<input type="number" name="length" value={defaultSwitchState ? defaultLength : length} onChange={(e) => { setLength(parseFloat(e.target.value)); updateProductWeightAndDimensions({ length: e.target.value }); }} />
							    	</div>
							    	<div className={styles.dimension_input}>
							        	<label>Width (in.)</label>
							        	<input type="number" name="width" value={defaultSwitchState ? defaultWidth : width} onChange={(e) => { setWidth(parseFloat(e.target.value)); updateProductWeightAndDimensions({ width: e.target.value }); }} />
							    	</div>
							      	<div className={styles.dimension_input}>
							        	<label>Height (in.)</label>
							        	<input type="number" name="height" value={defaultSwitchState ? defaultHeight : height} onChange={(e) => { setHeight(parseFloat(e.target.value)); updateProductWeightAndDimensions({ height: e.target.value }); }} />
							      	</div>
							      	<div className={styles.dimension_input}>
							        	<label>Weight (oz.)</label>
							        	<input type="number" name="weight" value={defaultSwitchState ? defaultWeight : weight} onChange={(e) => { setWeight(parseFloat(e.target.value)); updateProductWeightAndDimensions({ weight: e.target.value }); }} />
							      	</div>
							    </div>
								<div className={`login_buttons ${styles.back_to_feedback_button} fade_in`} onClick={() => setRenderProductDimensionsConfig(false)}>Feedback form</div> 
							</div>
						)}
					</div>
					<BackButton button_id={styles.edit_exchange_form_back_button} image_id={styles.edit_exchange_form_back_button_arrow} to={"home"} customBack={() => setShowSelectEligibleProducts(true) }/>
				</div>
			)}
		</>
	);
};

export default EditExchangeForm;